import React from 'react';
import { MapPin, Calendar, Car, Users, Luggage, CreditCard, Tag, Gift } from 'lucide-react';

const BookingDetails = ({ bookingData }) => {
  const {
    pickup,
    dropoff,
    date,
    time,
    vehicleName,
    passengers,
    luggage,
    extras = [],
    totalPaid,
    subtotal,
    paymentMethod,
    couponCode,
    discount,
    currency = 'EUR',
  } = bookingData;
  
  // Format price properly
  const formatPrice = (value) => {
    const num = parseFloat(value) || 0;
    return num.toFixed(2);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-xl p-6" data-testid="booking-details">
      <h2 className="font-semibold text-black text-lg mb-4">Booking Details</h2>

      <div className="space-y-4">
        {/* Route */}
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Route</p>
            <p className="text-sm font-medium text-black">{pickup}</p>
            <p className="text-xs text-gray-400">to</p>
            <p className="text-sm font-medium text-black">{dropoff}</p>
          </div>
        </div>

        {/* Date & Time */}
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Date & Time</p>
            <p className="text-sm font-medium text-black">
              {formatDate(date)} at {time}
            </p>
            <p className="text-xs text-gray-400">Turkey Time (GMT+3)</p>
          </div>
        </div>

        {/* Vehicle */}
        <div className="flex items-start gap-3">
          <Car className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Vehicle</p>
            <p className="text-sm font-medium text-black">{vehicleName}</p>
          </div>
        </div>

        {/* Passengers & Luggage */}
        <div className="flex items-start gap-6">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Passengers</p>
              <p className="text-sm font-medium text-black">{passengers}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Luggage className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Luggage</p>
              <p className="text-sm font-medium text-black">{luggage}</p>
            </div>
          </div>
        </div>

        {/* Extras */}
        {extras.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Extras</p>
            <ul className="space-y-1">
              {extras.map((extra, index) => (
                <li key={index} className="text-sm text-black">
                  • {extra.name || extra.label} {extra.quantity > 1 && `(×${extra.quantity})`}
                  {extra.is_gift && <span className="ml-2 text-xs text-green-600 font-medium">(FREE)</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Included Free Services */}
        <div className="pt-3 border-t border-gray-100">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Gift className="h-3.5 w-3.5 text-green-600" />
              <p className="text-xs text-green-700 uppercase tracking-wider font-semibold">Included Free</p>
            </div>
            <div className="text-sm text-green-700 flex justify-between items-center">
              <span>Meet & Greet</span>
              <span className="flex items-center gap-1">
                <span className="text-gray-400 line-through text-xs">€15</span>
                <span className="font-semibold text-green-600">FREE</span>
              </span>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Payment</p>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {paymentMethod === 'paypal' ? 'PayPal' : 
                   paymentMethod === 'cash' ? 'Cash to Driver' : 
                   paymentMethod === 'stripe' ? 'Credit Card' : 'Credit Card'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="pt-3 border-t border-gray-100 space-y-2">
          {/* Subtotal if there's a discount */}
          {couponCode && parseFloat(discount) > 0 && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Subtotal</span>
                <span className="text-sm text-gray-600">€{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center text-green-600">
                <span className="text-sm flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  Coupon ({couponCode})
                </span>
                <span className="text-sm">-€{formatPrice(discount)}</span>
              </div>
            </>
          )}
          
          {/* Total */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <span className="text-base font-semibold text-black">Total Paid</span>
            <span className="text-2xl font-bold text-[#FF8C00] font-display">
              €{formatPrice(totalPaid)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
